import React, { useState, useEffect } from 'react';
import { Turnstile } from "@marsidev/react-turnstile";
import './LightningSubscription.css';

import fnnLogo from "../../client-dashboard/client-dashboard-assets/FNNLogoWithBlackFont-06.png"

// ─── Types ────────────────────────────────────────────────────────────────────
interface Location {
  location_id: number;
  location_name: string;
}

interface TermsAndConditions {
  version: number;
  body: string;
  created_at: string;
}

type View = 'menu' | 'subscribe' | 'request' | 'update' | 'unsubscribe';
type Status = 'idle' | 'loading' | 'success' | 'error';
type OtpStatus = 'idle' | 'sending' | 'sent' | 'verifying' | 'verified' | 'error';

// ─── Constants ────────────────────────────────────────────────────────────────
const TURNSTILE_SITE_KEY = "0x4AAAAAACiHB4c31UPAbHHe";
const API_BASE = "http://localhost:8000/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fetchCsrfToken = async (): Promise<string | null> => {
  const response = await fetch('http://localhost:8000/api/csrf-token/', {
    method: 'GET',
    credentials: "include",
  });
  if (response.ok) {
    const data = await response.json();
    return data.csrfToken;
  } else {
    console.error('Failed to fetch CSRF token');
    return null;
  }
};

const normalizePhoneNumber = (input: string): string => {
  if (!input) return "";
  const value = input.trim();
  if (value.startsWith("+")) return "+" + value.slice(1).replace(/\D/g, "");
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) return "+1" + digits;
  if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
  return "+" + digits;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StormIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 20C14 14.477 18.477 10 24 10C28.663 10 32.596 13.096 33.699 17.35C36.668 17.956 39 20.618 39 23.833C39 27.5 36.038 30.5 32.333 30.5H15.667C11.962 30.5 9 27.5 9 23.833C9 21.192 10.612 18.92 13 17.958"
      stroke="#28AAE1" strokeWidth="2" strokeLinecap="round" />
    <path d="M26 30.5L22 37.5H27L23 44.5"
      stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BackArrow = ({ onClick }: { onClick: () => void }) => (
  <button className="ls-back-btn" onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    Back
  </button>
);

const LocationCheckbox = ({
  loc, checked, onChange
}: { loc: Location; checked: boolean; onChange: (id: number) => void }) => (
  <label className={`ls-checkbox-item ${checked ? 'ls-checked' : ''}`}>
    <div className={`ls-checkbox-box ${checked ? 'ls-checked' : ''}`}>
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
    <input type="checkbox" className="ls-checkbox-hidden" checked={checked} onChange={() => onChange(loc.location_id)} />
    <span className={`ls-checkbox-text ${checked ? 'ls-checked' : ''}`}>{loc.location_name}</span>
  </label>
);

const StatusBanner = ({ status, message }: { status: 'success' | 'error'; message: string }) => (
  <div className={`ls-banner ${status}`}>{message}</div>
);

// ─── Terms and Conditions ─────────────────────────────────────────────────────

const TermsAndConditionsField = ({
  agreed, onChange, terms,
}: {
  agreed: boolean;
  onChange: (val: boolean) => void;
  terms: TermsAndConditions | null;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="ls-field-last ls-terms-wrapper">
      <div className="ls-terms-box">
        <button type="button" className="ls-terms-toggle" onClick={() => setExpanded(prev => !prev)}>
          <span className="ls-terms-title">Terms and Conditions</span>
          <svg className={`ls-terms-chevron ${expanded ? 'ls-terms-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="#28AAE1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {expanded && (
          <div className="ls-terms-body">
            {terms
              ? <p style={{ whiteSpace: 'pre-wrap' }}>{terms.body}</p>
              : <p>Loading Terms and Conditions...</p>
            }
          </div>
        )}
      </div>
      <label className={`ls-checkbox-item ls-terms-checkbox-row ${agreed ? 'ls-checked' : ''}`}>
        <div className={`ls-checkbox-box ${agreed ? 'ls-checked' : ''}`}>
          {agreed && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <input type="checkbox" className="ls-checkbox-hidden" checked={agreed} onChange={e => onChange(e.target.checked)} />
        <span className={`ls-checkbox-text ${agreed ? 'ls-checked' : ''}`}>
          I have read and agree to the <span className="ls-terms-highlight">Terms and Conditions</span>
        </span>
      </label>
    </div>
  );
};

// ─── Menu View ────────────────────────────────────────────────────────────────

const MenuView = ({ onSelect }: { onSelect: (v: View) => void }) => {
  const options = [
    { view: 'subscribe' as View, icon: '📬', label: 'Subscribe to Email Alerts', desc: 'Sign up for email alerts at your locations.' },
    { view: 'request' as View, icon: '📱', label: 'Request Subscription to SMS Alerts', desc: 'Request to be signed up for SMS alerts at your locations. Subject to approval.' },
    { view: 'update' as View, icon: '📍', label: 'Update Locations', desc: "Manage your subscription's locations." },
    { view: 'unsubscribe' as View, icon: '🚫', label: 'Unsubscribe', desc: 'Remove yourself from all alerts.' },
  ];

  return (
    <div>
      <div className="ls-menu-header">
        <h1 className="ls-menu-title"><StormIcon /> Lightning Alerts</h1>
        <p className="ls-menu-subtitle">Real-time lightning notifications for your locations</p>
      </div>
      <div className="ls-menu-options">
        {options.map(({ view, icon, label, desc }) => (
          <button key={view} className="ls-menu-option-btn" onClick={() => onSelect(view)}>
            <span className="ls-menu-option-icon">{icon}</span>
            <div>
              <div className="ls-menu-option-label">{label}</div>
              <div className="ls-menu-option-desc">{desc}</div>
            </div>
            <svg className="ls-menu-option-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4L10 8L6 12" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Subscribe View ───────────────────────────────────────────────────────────

const SubscribeView = ({
  onBack, locations, terms,
}: {
  onBack: () => void;
  locations: Location[];
  terms: TermsAndConditions | null;
}) => {
  const [form, setForm] = useState({ name: '', email: '', phone_number: '' });
  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // ── OTP state ─────────────────────────────────────────────────────────────
  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);

  // ── Turnstile (single, fires on Subscribe click) ───────────────────────────
  const [showTurnstile, setShowTurnstile] = useState(false);

  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Step 1: validate form → show Turnstile ─────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) {
      setStatus('error'); setMessage('Please enter your email.'); return;
    }
    if (selectedLocs.length === 0) {
      setStatus('error'); setMessage('Please select at least one location.'); return;
    }
    if (!agreedToTerms) {
      setStatus('error'); setMessage('You must agree to the Terms and Conditions to subscribe.'); return;
    }
    // Clear any previous error and show Turnstile
    setStatus('idle');
    setMessage('');
    setShowTurnstile(true);
  };

  // ── Step 2: Turnstile passes → send OTP ───────────────────────────────────
  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');
    setOtpMessage('');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, turnstileToken: token, flow: 'subscribe' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`A 6-digit verification code was sent to ${form.email}. It expires in 10 minutes.`);
    } catch (err: any) {
      const errorMessage =
        err.message || 'Could not send verification code.';

      // These errors happen BEFORE an OTP is actually sent,
      // so show them on the main form instead of the OTP screen.
      if (
        errorMessage.includes('already subscribed')
      ) {
        setOtpStatus('idle');
        setStatus('error');
        setMessage(errorMessage);
        return;
      }

      setOtpStatus('error');
      setOtpMessage(errorMessage);
    }
  };

  // ── Step 3: verify OTP → subscribe ────────────────────────────────────────
  const handleVerifyAndSubscribe = async () => {
    if (otpCode.length !== 6) {
      setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return;
    }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    // Verify OTP
    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const token = verifyData.verified_token;
      setVerifiedToken(token);
      setOtpStatus('verified');

      // Subscribe immediately after verification
      setStatus('loading');
      const subscribeRes = await fetch(`${API_BASE}/public/sms_subscribe/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          ...form,
          phone_number: normalizePhoneNumber(form.phone_number),
          locations: selectedLocs,
          terms_version: terms?.version,
          verified_token: token,
        }),
      });
      const subscribeData = await subscribeRes.json();
      if (!subscribeRes.ok) throw new Error(subscribeData.error || 'Subscription failed.');
      setStatus('success');
      setMessage("You're subscribed! You'll receive alerts when storms are detected near your locations.");
    } catch (err: any) {
      // If OTP was fine but subscribe failed, keep verified state so user doesn't re-verify
      if (otpStatus === 'verified' || verifiedToken) {
        setStatus('error'); setMessage(err.message || 'Subscription failed.');
      } else {
        setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
      }
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const handleResendOtp = () => {
    setOtpStatus('idle');
    setOtpCode('');
    setOtpMessage('');
    setVerifiedToken(null);
    setShowTurnstile(true);
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">✅</div>
        <h2 className="ls-success-title">Subscribed!</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  // ── OTP entry screen (shown after Turnstile passes) ────────────────────────
  if (otpStatus === 'sent' || otpStatus === 'verifying' || otpStatus === 'error') return (
    <div>
      <BackArrow onClick={() => {
        setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
        setVerifiedToken(null); setShowTurnstile(false);
      }} />
      <h2 className="ls-view-title">Verify your email</h2>
      <p className="ls-view-subtitle">
        We sent a 6-digit code to <strong>{form.email}</strong>. Enter it below to complete your subscription.
      </p>

      <div className="ls-otp-standalone">
        <div className="ls-otp-verify-row">
          <input
            className="ls-input ls-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={otpCode}
            onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        {otpMessage && (
          <p className={`ls-otp-msg ${otpStatus === 'error' ? 'ls-otp-msg-error' : 'ls-otp-msg-info'}`}>
            {otpMessage}
          </p>
        )}

        <button
          type="button"
          className="ls-submit-btn"
          style={{ marginTop: '16px' }}
          disabled={otpStatus === 'verifying'}
          onClick={handleVerifyAndSubscribe}
        >
          {otpStatus === 'verifying' ? 'Verifying…' : 'Confirm & Subscribe'}
        </button>

        <button type="button" className="ls-resend-btn" onClick={handleResendOtp}>
          Didn&apos;t receive a code? Resend
        </button>
      </div>
    </div>
  );

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Subscribe to Alerts</h2>
      <p className="ls-view-subtitle">Get notified via email when lightning storms start and end near your locations.</p>

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Name *</label>
          <input className="ls-input" placeholder="Your name" value={form.name} required
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>

        <div className="ls-field">
          <label className="ls-label">Email *</label>
          <input className="ls-input" type="email" placeholder="you@example.com" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>

        {/* <div className="ls-field-last">
          <label className="ls-label">Phone Number</label>
          <input className="ls-input" type="tel" placeholder="+1 (555) 000-0000" value={form.phone_number}
            onChange={e => setForm({ ...form, phone_number: e.target.value })} />
          <p className="ls-field-hint">SMS alerts are subject to approval. You will receive an email within a few businness days notifying you of our response.</p>
        </div> */}

        <div className="ls-field-last">
          <label className="ls-label ls-locations-label">Locations *</label>
          <div className="ls-location-scroll">
            {locations.length === 0
              ? <p className="ls-locations-loading">Loading locations...</p>
              : locations.map(loc => (
                <LocationCheckbox key={loc.location_id} loc={loc}
                  checked={selectedLocs.includes(loc.location_id)}
                  onChange={toggleLoc} />
              ))
            }
          </div>
        </div>

        <TermsAndConditionsField agreed={agreedToTerms} onChange={setAgreedToTerms} terms={terms} />

        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess}
            />
          </div>
        )}

        {status === 'error' && <StatusBanner status="error" message={message} />}

        <button type="submit" className="ls-submit-btn" disabled={status === 'loading' || showTurnstile}>
          {showTurnstile ? 'Sending code…' : 'Subscribe'}
        </button>
      </form>
    </div>
  );
};

// ─── Update View ──────────────────────────────────────────────────────────────

const UpdateView = ({ onBack, locations }: { onBack: () => void; locations: Location[] }) => {
  const [identifier, setIdentifier] = useState({ email: '', phone_number: '' });
  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  // ── NEW OTP state ──────────────────────────────────────────────────────────
  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.email) {
      setStatus('error'); setMessage('Please enter your email.'); return;
    }
    if (selectedLocs.length === 0) {
      setStatus('error'); setMessage('Please select at least one location.'); return;
    }
    setStatus('idle'); setMessage('');
    setShowTurnstile(true);
  };

  // ── Step 2: Turnstile → send OTP (same as SubscribeView) ──────────────────
  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');
    setOtpMessage('');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: identifier.email, turnstileToken: token, flow: 'update' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`A 6-digit verification code was sent to ${identifier.email}.`);
    } catch (err: any) {
      const errorMessage =
        err.message || 'Could not send verification code.';

      // No OTP was sent, so stay on the form screen
      if (
        errorMessage.includes('No subscription found')
      ) {
        setOtpStatus('idle');
        setStatus('error');
        setMessage(errorMessage);
        return;
      }

      setOtpStatus('error');
      setOtpMessage(errorMessage);
    }
  };

  // ── Step 3: verify OTP → update locations ─────────────────────────────────
  const handleVerifyAndUpdate = async () => {
    if (otpCode.length !== 6) {
      setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return;
    }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: identifier.email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const token = verifyData.verified_token;
      setVerifiedToken(token);
      setOtpStatus('verified');
      setStatus('loading');

      const res = await fetch(`${API_BASE}/public/sms_subscribers/update_locations/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          email: identifier.email,
          locations: selectedLocs,
          verified_token: token,   // ← replaces turnstileToken
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed.');
      setStatus('success');
      setMessage('Your locations have been updated successfully.');
    } catch (err: any) {
      if (verifiedToken || otpStatus === 'verified') {
        setStatus('error'); setMessage(err.message || 'Update failed.');
      } else {
        setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
      }
    }
  };

  const handleResendOtp = () => {
    setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
    setVerifiedToken(null); setShowTurnstile(true);
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">📍</div>
        <h2 className="ls-success-title">Locations Updated!</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  // ── OTP entry screen ───────────────────────────────────────────────────────
  if (otpStatus === 'sent' || otpStatus === 'verifying' || otpStatus === 'error') return (
    <div>
      <BackArrow onClick={() => {
        setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
        setVerifiedToken(null); setShowTurnstile(false);
      }} />
      <h2 className="ls-view-title">Verify your email</h2>
      <p className="ls-view-subtitle">
        We sent a 6-digit code to <strong>{identifier.email}</strong>. Enter it below to update your locations.
      </p>
      <div className="ls-otp-standalone">
        <div className="ls-otp-verify-row">
          <input
            className="ls-input ls-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={otpCode}
            onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
          />
        </div>
        {otpMessage && (
          <p className={`ls-otp-msg ${otpStatus === 'error' ? 'ls-otp-msg-error' : 'ls-otp-msg-info'}`}>
            {otpMessage}
          </p>
        )}
        <button
          type="button"
          className="ls-submit-btn"
          style={{ marginTop: '16px' }}
          disabled={otpStatus === 'verifying'}
          onClick={handleVerifyAndUpdate}
        >
          {otpStatus === 'verifying' ? 'Verifying…' : 'Confirm & Update Locations'}
        </button>
        <button type="button" className="ls-resend-btn" onClick={handleResendOtp}>
          Didn&apos;t receive a code? Resend
        </button>
      </div>
    </div>
  );

  // ── Main form (unchanged except button label) ──────────────────────────────
  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Update Locations</h2>
      <p className="ls-view-subtitle">Enter your account details and select locations you would like coverage.</p>
      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input className="ls-input" type="email" placeholder="you@example.com" value={identifier.email}
            onChange={e => setIdentifier({ ...identifier, email: e.target.value })} />
        </div>
        <div className="ls-field-last">
          <label className="ls-label ls-locations-label">Update Locations *</label>
          <div className="ls-location-scroll">
            {locations.map(loc => (
              <LocationCheckbox key={loc.location_id} loc={loc}
                checked={selectedLocs.includes(loc.location_id)}
                onChange={toggleLoc} />
            ))}
          </div>
        </div>
        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess}  // ← was calling submit() directly
            />
          </div>
        )}
        {status === 'error' && <StatusBanner status="error" message={message} />}
        <button type="submit" className="ls-submit-btn" disabled={status === 'loading' || showTurnstile}>
          {showTurnstile ? 'Sending code…' : 'Update Locations'}
        </button>
      </form>
    </div>
  );
};

// ─── Unsubscribe View ─────────────────────────────────────────────────────────

const UnsubscribeView = ({ onBack }: { onBack: () => void }) => {
  const [form, setForm] = useState({ email: '' });
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  // ── NEW OTP state ──────────────────────────────────────────────────────────
  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) {
      setStatus('error'); setMessage('Please enter your email.'); return;
    }
    setStatus('idle'); setMessage('');
    setShowTurnstile(true);
  };

  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, turnstileToken: token, flow: 'unsubscribe' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`A 6-digit verification code was sent to ${form.email}.`);
    } catch (err: any) {
      const errorMessage =
        err.message || 'Could not send verification code.';

      // No OTP was sent, so keep user on main form
      if (
        errorMessage.includes('No subscription found')
      ) {
        setOtpStatus('idle');
        setStatus('error');
        setMessage(errorMessage);
        return;
      }

      setOtpStatus('error');
      setOtpMessage(errorMessage);
    }
  };

  const handleVerifyAndUnsubscribe = async () => {
    if (otpCode.length !== 6) {
      setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return;
    }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const token = verifyData.verified_token;
      setVerifiedToken(token);
      setOtpStatus('verified');
      setStatus('loading');

      const res = await fetch(`${API_BASE}/public/sms_unsubscribe/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          email: form.email,
          verified_token: token,   // ← replaces turnstileToken
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unsubscribe failed.');
      setStatus('success');
      setMessage('You have been successfully unsubscribed from all lightning alerts.');
    } catch (err: any) {
      if (verifiedToken || otpStatus === 'verified') {
        setStatus('error'); setMessage(err.message || 'Unsubscribe failed.');
      } else {
        setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
      }
    }
  };

  const handleResendOtp = () => {
    setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
    setVerifiedToken(null); setShowTurnstile(true);
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">👋</div>
        <h2 className="ls-success-title">Unsubscribed</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  // ── OTP entry screen ───────────────────────────────────────────────────────
  if (otpStatus === 'sent' || otpStatus === 'verifying' || otpStatus === 'error') return (
    <div>
      <BackArrow onClick={() => {
        setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
        setVerifiedToken(null); setShowTurnstile(false);
      }} />
      <h2 className="ls-view-title">Verify your email</h2>
      <p className="ls-view-subtitle">
        We sent a 6-digit code to <strong>{form.email}</strong>. Enter it to confirm your unsubscribe request.
      </p>
      <div className="ls-otp-standalone">
        <div className="ls-otp-verify-row">
          <input
            className="ls-input ls-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={otpCode}
            onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
          />
        </div>
        {otpMessage && (
          <p className={`ls-otp-msg ${otpStatus === 'error' ? 'ls-otp-msg-error' : 'ls-otp-msg-info'}`}>
            {otpMessage}
          </p>
        )}
        <button
          type="button"
          className="ls-submit-btn danger"
          style={{ marginTop: '16px' }}
          disabled={otpStatus === 'verifying'}
          onClick={handleVerifyAndUnsubscribe}
        >
          {otpStatus === 'verifying' ? 'Verifying…' : 'Confirm & Unsubscribe'}
        </button>
        <button type="button" className="ls-resend-btn" onClick={handleResendOtp}>
          Didn&apos;t receive a code? Resend
        </button>
      </div>
    </div>
  );

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Unsubscribe</h2>
      <p className="ls-view-subtitle">Enter your email to remove yourself from all lightning alerts.</p>
      <div className="ls-warning-box">
        ⚠️ This will permanently delete your subscription and all associated locations.
      </div>
      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input className="ls-input" type="email" placeholder="you@example.com" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>
        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess}  // ← was calling submit() directly
            />
          </div>
        )}
        {status === 'error' && <StatusBanner status="error" message={message} />}
        <button type="submit" className="ls-submit-btn danger" disabled={status === 'loading' || showTurnstile}>
          {showTurnstile ? 'Sending code…' : 'Unsubscribe'}
        </button>
      </form>
    </div>
  );
};

// ─── SMS Request View ─────────────────────────────────────────────────────────

const SmsRequestView = ({
  onBack,
  locations,
  terms,
}: {
  onBack: () => void;
  locations: Location[];
  terms: TermsAndConditions | null;
}) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone_number: '',
  });

  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // OTP
  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);

  // Turnstile
  const [showTurnstile, setShowTurnstile] = useState(false);

  // Submit status
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );

  // STEP 1 — validate form
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setStatus('error');
      setMessage('Please enter your name.');
      return;
    }

    if (!form.email.trim()) {
      setStatus('error');
      setMessage('Please enter your email.');
      return;
    }

    if (!form.phone_number.trim()) {
      setStatus('error');
      setMessage('Please enter your phone number.');
      return;
    }

    if (selectedLocs.length === 0) {
      setStatus('error');
      setMessage('Please select at least one location.');
      return;
    }

    if (!agreedToTerms) {
      setStatus('error');
      setMessage('You must agree to the Terms and Conditions.');
      return;
    }

    setStatus('idle');
    setMessage('');
    setShowTurnstile(true);
  };

  // STEP 2 — Turnstile → send OTP
  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);

    setOtpStatus('sending');
    setOtpMessage('');

    const csrfToken = await fetchCsrfToken();

    if (!csrfToken) {
      setOtpStatus('error');
      setOtpMessage('Could not get CSRF token.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          email: form.email,
          turnstileToken: token,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send verification code.');
      }

      setOtpStatus('sent');

      setOtpMessage(
        `A 6-digit verification code was sent to ${form.email}.`
      );
    } catch (err: any) {
      setOtpStatus('error');
      setOtpMessage(
        err.message || 'Failed to send verification code.'
      );
    }
  };

  // STEP 3 — Verify OTP + submit request
  const handleVerifyAndSubmit = async () => {
    if (otpCode.length !== 6) {
      setOtpStatus('error');
      setOtpMessage('Please enter the 6-digit code.');
      return;
    }

    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();

    if (!csrfToken) {
      setOtpStatus('error');
      setOtpMessage('Could not get CSRF token.');
      return;
    }

    try {
      // Verify OTP
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          email: form.email,
          code: otpCode,
        }),
      });

      const verifyData = await verifyRes.json();

      if (!verifyRes.ok) {
        throw new Error(
          verifyData.error || 'Verification failed.'
        );
      }

      const token = verifyData.verified_token;

      setVerifiedToken(token);
      setOtpStatus('verified');

      // Submit SMS request
      setStatus('loading');

      const submitRes = await fetch(
        `${API_BASE}/public/sms_sub_request/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            phone_number: normalizePhoneNumber(form.phone_number),
            locations: selectedLocs,
            terms_version: terms?.version,
            verified_token: token,
          }),
        }
      );

      const submitData = await submitRes.json();

      if (!submitRes.ok) {
        throw new Error(
          submitData.error || 'Request submission failed.'
        );
      }

      setStatus('success');

      setMessage(
        submitData.message ||
        'Your SMS alert request has been submitted successfully.'
      );
    } catch (err: any) {
      if (verifiedToken || otpStatus === 'verified') {
        setStatus('error');
        setMessage(
          err.message || 'Request submission failed.'
        );
      } else {
        setOtpStatus('error');
        setOtpMessage(
          err.message || 'Verification failed.'
        );
      }
    }
  };

  const handleResendOtp = () => {
    setOtpStatus('idle');
    setOtpCode('');
    setOtpMessage('');
    setVerifiedToken(null);
    setShowTurnstile(true);
  };

  // SUCCESS SCREEN
  if (status === 'success') {
    return (
      <div>
        <BackArrow onClick={onBack} />

        <div className="ls-success-screen">
          <div className="ls-success-icon">📱</div>

          <h2 className="ls-success-title">
            SMS Request Submitted
          </h2>

          <p className="ls-success-message">
            {message}
          </p>
        </div>
      </div>
    );
  }

  // OTP SCREEN
  if (
    otpStatus === 'sent' ||
    otpStatus === 'verifying' ||
    otpStatus === 'error'
  ) {
    return (
      <div>
        <BackArrow
          onClick={() => {
            setOtpStatus('idle');
            setOtpCode('');
            setOtpMessage('');
            setVerifiedToken(null);
            setShowTurnstile(false);
          }}
        />

        <h2 className="ls-view-title">
          Verify your email
        </h2>

        <p className="ls-view-subtitle">
          We sent a verification code to{' '}
          <strong>{form.email}</strong>.
        </p>

        <div className="ls-otp-standalone">
          <div className="ls-otp-verify-row">
            <input
              className="ls-input ls-otp-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={e =>
                setOtpCode(
                  e.target.value.replace(/\D/g, '')
                )
              }
            />
          </div>

          {otpMessage && (
            <p
              className={`ls-otp-msg ${otpStatus === 'error'
                ? 'ls-otp-msg-error'
                : 'ls-otp-msg-info'
                }`}
            >
              {otpMessage}
            </p>
          )}

          <button
            type="button"
            className="ls-submit-btn"
            style={{ marginTop: '16px' }}
            disabled={otpStatus === 'verifying'}
            onClick={handleVerifyAndSubmit}
          >
            {otpStatus === 'verifying'
              ? 'Verifying...'
              : 'Confirm & Submit Request'}
          </button>

          <button
            type="button"
            className="ls-resend-btn"
            onClick={handleResendOtp}
          >
            Didn&apos;t receive a code? Resend
          </button>
        </div>
      </div>
    );
  }

  // MAIN FORM
  return (
    <div>
      <BackArrow onClick={onBack} />

      <h2 className="ls-view-title">
        Request SMS Alerts
      </h2>

      <p className="ls-view-subtitle">
        Request approval to receive lightning alerts via SMS.
        Your request will be reviewed manually.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Name *</label>

          <input
            className="ls-input"
            placeholder="Your name"
            value={form.name}
            onChange={e =>
              setForm({
                ...form,
                name: e.target.value,
              })
            }
          />
        </div>

        <div className="ls-field">
          <label className="ls-label">Email *</label>

          <input
            className="ls-input"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={e =>
              setForm({
                ...form,
                email: e.target.value,
              })
            }
          />
        </div>

        <div className="ls-field-last">
          <label className="ls-label">
            Phone Number *
          </label>

          <input
            className="ls-input"
            type="tel"
            placeholder="+1 (555) 000-0000"
            value={form.phone_number}
            onChange={e =>
              setForm({
                ...form,
                phone_number: e.target.value,
              })
            }
          />

          <p className="ls-field-hint">
            SMS requests are manually reviewed before approval.
          </p>
        </div>

        <div className="ls-field-last">
          <label className="ls-label ls-locations-label">
            Locations *
          </label>

          <div className="ls-location-scroll">
            {locations.length === 0 ? (
              <p className="ls-locations-loading">
                Loading locations...
              </p>
            ) : (
              locations.map(loc => (
                <LocationCheckbox
                  key={loc.location_id}
                  loc={loc}
                  checked={selectedLocs.includes(
                    loc.location_id
                  )}
                  onChange={toggleLoc}
                />
              ))
            )}
          </div>
        </div>

        <TermsAndConditionsField
          agreed={agreedToTerms}
          onChange={setAgreedToTerms}
          terms={terms}
        />

        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              options={{
                theme: 'light',
                size: 'normal',
                language: 'auto',
              }}
              onSuccess={handleTurnstileSuccess}
            />
          </div>
        )}

        {status === 'error' && (
          <StatusBanner
            status="error"
            message={message}
          />
        )}

        <button
          type="submit"
          className="ls-submit-btn"
          disabled={
            status === 'loading' || showTurnstile
          }
        >
          {showTurnstile
            ? 'Sending code...'
            : 'Request SMS Alerts'}
        </button>
      </form>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const LightningSubscription = () => {
  const [view, setView] = useState<View>('menu');
  const [locations, setLocations] = useState<Location[]>([]);
  const [terms, setTerms] = useState<TermsAndConditions | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/public/sms_locations`)
      .then(r => r.ok ? r.json() : [])
      .then(setLocations)
      .catch(() => setLocations([]));

    fetch(`${API_BASE}/public/sms_terms`)
      .then(r => r.ok ? r.json() : null)
      .then(setTerms)
      .catch(() => setTerms(null));
  }, []);

  return (
    <div className="ls-page">
      <div className="ls-header">
        <img
          src={fnnLogo}
          alt="FNN Logo"
          className="fnn-header-logo ls-logo"
          onClick={() => window.open('https://fireneuralnetwork.com/', '_blank', 'noopener,noreferrer')}
        />
        <h1 className="ls-header-title">FNN™ Alert System</h1>
      </div>

      <div className="ls-center">
        <div className="ls-panel">
          {view === 'menu' && <MenuView onSelect={setView} />}
          {view === 'subscribe' && (
            <SubscribeView
              onBack={() => setView('menu')}
              locations={locations}
              terms={terms}
            />
          )}

          {view === 'request' && (
            <SmsRequestView
              onBack={() => setView('menu')}
              locations={locations}
              terms={terms}
            />
          )}

          {view === 'update' && <UpdateView onBack={() => setView('menu')} locations={locations} />}
          {view === 'unsubscribe' && <UnsubscribeView onBack={() => setView('menu')} />}
        </div>
      </div>
    </div>
  );
};

export default LightningSubscription;